import { logger } from "$lib/utils/logger";
import { createFetchWithRetry } from "$lib/utils/fetchHelpers";
import type { ContentApiResponse } from "./contentTypes";
import { reportError } from "$lib/errorHandling/errorReporter";
import { requestSwCacheVideos } from "$lib/utils/cacheHelpers";
import { readCachedContent } from "./contentCache";
import { contentApiUrl } from "./contentApi";
import { startupJitterMs } from "./startupJitter";
import { isPreviewMode } from "$lib/utils/previewMode";
import { nanoid } from "nanoid";

const useStandaloneMode =
  import.meta.env.VITE_STANDALONE_MODE === "true" &&
  import.meta.env.MODE !== "production"; // Dev only - production must always hit real API
const standaloneUrl =
  "/fake-content-api/contentApi_experimental_sampleData.json";

let currentContent: ContentApiResponse | null = null;
let nextContent: ContentApiResponse | null = null;
// True once the loop has played currentContent through, or when it came from cache.
let currentIsStale = false;
let inFlightFetch: Promise<ContentApiResponse> | null = null;
let terminalFailureDetected = false;

const TERMINAL_STATUS_CODES = [404, 410];
const TERMINAL_ERROR_MESSAGE = "[ContentFetch] Display permanently unavailable";

const fetch = createFetchWithRetry({
  logContext: "Elev8Api-Content",
  maxRetriesCount: 9,
  retryOnHttpStatusCodes: TERMINAL_STATUS_CODES,
});

async function getLatestContent(
  displayId: string
): Promise<ContentApiResponse> {
  if (nextContent) {
    logger.info("[ContentFetch] nextContent available!");
    currentContent = nextContent;
    nextContent = null;
    currentIsStale = false;
    return currentContent;
  }
  if (currentContent) {
    currentIsStale = true;
    return currentContent;
  }
  logger.warn(
    "[ContentFetch] missing next and current content (probably initial load). Fetching.."
  );
  currentContent = await fetchContent(displayId);
  return currentContent;
}

function shouldSwapContent(): boolean {
  return currentIsStale && nextContent !== null;
}

// Must run before the content components mount, or getLatestContent races it.
async function primeContentFromCache(displayId: string): Promise<void> {
  if (isPreviewMode() || currentContent) return;
  const cached = await readCachedContent(displayId);
  if (!cached) return;
  try {
    applyContentDefaults(cached);
  } catch (error) {
    logger.warn("[ContentFetch] Cached content unusable; fetching.", error);
    return;
  }
  currentContent = cached;
  currentIsStale = true;
  logger.info("[ContentFetch] Primed content from cache.");
  void fetchAndStageNextContent(displayId, startupJitterMs());
}

async function fetchAndStageNextContent(displayId: string, delayMs = 0) {
  if (nextContent || inFlightFetch) {
    logger.info(
      "[ContentFetch] Content already staged or in flight; skipping fetchAndStageNextContent."
    );
    return;
  }
  logger.info("[ContentFetch] Updating next content");
  let data: ContentApiResponse;
  try {
    data = await fetchContent(displayId, delayMs);
  } catch {
    return;
  }
  populateNextContent(data);
}

async function fetchContent(
  displayId: string,
  delayMs = 0
): Promise<ContentApiResponse> {
  if (inFlightFetch) return inFlightFetch;
  inFlightFetch = fetchContentRaw(displayId, delayMs).finally(() => {
    inFlightFetch = null;
  });
  return inFlightFetch;
}

async function fetchContentRaw(
  displayId: string,
  delayMs: number
): Promise<ContentApiResponse> {
  if (delayMs > 0) {
    logger.info(
      `[ContentFetch] Delaying refresh ${Math.round(delayMs / 1000)}s to spread the fleet reboot.`
    );
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  if (terminalFailureDetected) {
    throw new Error(TERMINAL_ERROR_MESSAGE);
  }
  const url = useStandaloneMode
    ? standaloneUrl
    : contentApiUrl(displayId, isPreviewMode());
  try {
    logger.debug(`[ContentFetch] Fetching content from ${url}`);
    const response = await fetch(url);
    if (!response.ok) {
      if (TERMINAL_STATUS_CODES.includes(response.status)) {
        markTerminal(response.status);
        throw new Error(TERMINAL_ERROR_MESSAGE);
      }
      throw new Error(
        `[ContentFetch] fetchContent - Network response was not ok: ${response.status} ${response.statusText}`
      );
    }
    if (useStandaloneMode) {
      logger.warn(
        "[ContentFetch] Standalone mode enabled, using sample content data."
      );
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate delay for demo purposes
    }

    const rawData: ContentApiResponse = await response.json(); //FIXME: Validate content (Zod, json schema, openapi)
    logger.debug("[ContentFetch] fetchContent response json", rawData);
    if (!rawData) {
      throw new Error("[ContentFetch] fetchContent - Invalid Content Data");
    }
    applyContentDefaults(rawData);
    const data = applyStandaloneTransforms(rawData);

    requestVideoCaching(data);
    logger.debug("[ContentFetch] - transformed data: ", data);
    return data;
  } catch (error) {
    if (error instanceof Error && error.message === TERMINAL_ERROR_MESSAGE) {
      throw error;
    }
    reportError("[ContentFetch] Failed to fetch content", error);
    throw error;
  }
}

function markTerminal(status: number) {
  terminalFailureDetected = true;
  logger.warn(`${TERMINAL_ERROR_MESSAGE}: status ${status}`);
  reportError(TERMINAL_ERROR_MESSAGE, undefined, {
    status_code: String(status),
  });
}

function populateNextContent(data: ContentApiResponse) {
  if (nextContent) {
    logger.warn(
      "[ContentFetch] next - nextContent already populated; overwriting it."
    );
  }
  nextContent = data;
}

function requestVideoCaching(data: ContentApiResponse) {
  requestSwCacheVideos(data.spots).catch((error) => {
    logger.error("[ContentFetch] Error requesting SW to cache videos", error);
  });
}

function applyContentDefaults(data: ContentApiResponse) {
  for (const spot of data.spots) {
    for (const content of spot.contents) {
      content.id ??= nanoid(); // unique id for {#key} in ContentRenderer
      content.duration ??= spot.duration; // consumed only by DataPipelinePlayEventPayload
    }
  }
}

function applyStandaloneTransforms(
  data: ContentApiResponse
): ContentApiResponse {
  if (!useStandaloneMode) return data;

  const weather = data.ticker?.find((t) => t.content_type === "weather");
  if (!weather) return data;

  if (weather.current) {
    weather.current.current = Math.floor(Math.random() * 41) - 20; // -20 to 20
    weather.current.feels_like =
      weather.current.current + Math.floor(Math.random() * 21) - 10; // ±10
  }

  weather.forecast.forEach((day) => {
    day.high = Math.floor(Math.random() * 41) - 20;
    day.low = day.high - Math.floor(Math.random() * 21); // 0-20 below high
  });

  return data;
}

export {
  getLatestContent,
  fetchAndStageNextContent,
  primeContentFromCache,
  shouldSwapContent,
};
